import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, ShieldAlert, Target, Eye, Archive, AlertTriangle,
  ChevronRight, Crosshair, Globe, Server, Skull, Bug as BugIcon,
  Network, Users, Zap
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { PageHeader } from "../components/PageHeader";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS as ALL_FRAMEWORKS, FW_SHORT, FW_COLORS } from "../constants/frameworks";

const severityConfig = {
  critical: { labelKey: "critical", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  high: { labelKey: "high", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  medium: { labelKey: "medium", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  low: { labelKey: "low", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" }
};

const statusConfig = {
  active: { labelKey: "threat.active", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  monitoring: { labelKey: "threat.monitoring", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  mitigated: { labelKey: "threat.mitigated", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  archived: { labelKey: "ux.archived", color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400" }
};

// status labels are accessed via t() using dedicated keys
const STATUS_LABEL_KEYS = {
  active: "threat.active",
  monitoring: "threat.monitoring",
  mitigated: "threat.mitigated",
  archived: "ux.archived"
};

const categoryIcons = {
  malware: Skull, social_engineering: Users, network: Network,
  human: Users, data: Server, third_party: Globe,
  vulnerability: BugIcon, apt: Crosshair
};

const CATEGORY_LABEL_KEYS = {
  malware: "threat.cat.malware", social_engineering: "threat.cat.social_engineering",
  network: "threat.cat.network", human: "threat.cat.human",
  data: "threat.cat.data", third_party: "threat.cat.third_party",
  vulnerability: "threat.cat.vulnerability", apt: "threat.cat.apt"
};

const MITRE_TACTICS = [
  "TA0001 - Initial Access", "TA0002 - Execution", "TA0003 - Persistence",
  "TA0004 - Privilege Escalation", "TA0005 - Defense Evasion", "TA0006 - Credential Access",
  "TA0007 - Discovery", "TA0008 - Lateral Movement", "TA0009 - Collection",
  "TA0010 - Exfiltration", "TA0011 - Command and Control", "TA0040 - Impact"
];

const ThreatIntelligence = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const { activeFrameworks } = useOrg();
  const orgFrameworks = ALL_FRAMEWORKS.filter((f) => activeFrameworks.includes(f.id));
  const [intel, setIntel] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIntel, setSelectedIntel] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterFramework, setFilterFramework] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [iocInput, setIocInput] = useState("");
  const [mitreInput, setMitreInput] = useState("");

  const [newIntel, setNewIntel] = useState({
    name: "", category: "malware", severity: "medium",
    description: "", source: "osint", iocs: [], mitre_techniques: [], affected_assets: [],
    relevant_frameworks: [],
  });

  const fetchIntel = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/threat-intel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntel(res.data);
    } catch (error) {
      console.error("Failed to fetch intel:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/threat-intel/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  }, [token]);

  useEffect(() => { fetchIntel(); fetchDashboard(); }, [fetchIntel, fetchDashboard]);

  const createIntel = async () => {
    if (!newIntel.name) { toast.error("Name is required"); return; }
    try {
      await axios.post(`${API}/threat-intel`, newIntel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Threat intel created");
      setShowCreate(false);
      setNewIntel({ name: "", category: "malware", severity: "medium", description: "", source: "osint", iocs: [], mitre_techniques: [], affected_assets: [], relevant_frameworks: [] });
      fetchIntel();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to create intel");
    }
  };

  const updateStatus = async (intelId, status) => {
    try {
      await axios.put(`${API}/threat-intel/${intelId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchIntel();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const addIOC = () => {
    if (iocInput && !newIntel.iocs.includes(iocInput)) {
      setNewIntel({ ...newIntel, iocs: [...newIntel.iocs, iocInput] });
      setIocInput("");
    }
  };

  const addMitre = () => {
    if (mitreInput && !newIntel.mitre_techniques.includes(mitreInput)) {
      setNewIntel({ ...newIntel, mitre_techniques: [...newIntel.mitre_techniques, mitreInput] });
      setMitreInput("");
    }
  };

  const filtered = intel.filter(i => {
    if (filterSeverity !== "all" && i.severity !== filterSeverity) return false;
    if (filterCategory !== "all" && i.category !== filterCategory) return false;
    if (filterFramework !== "all" && !(i.relevant_frameworks || []).includes(filterFramework)) return false;
    if (searchQuery && !i.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const severityChartData = dashboard?.by_severity
    ? Object.entries(dashboard.by_severity).map(([k, v]) => ({
        name: t(severityConfig[k]?.labelKey) || k, value: v,
        color: k === "critical" ? "#EF4444" : k === "high" ? "#F97316" : k === "medium" ? "#EAB308" : "#22C55E"
      }))
    : [];

  const categoryChartData = dashboard?.by_category
    ? Object.entries(dashboard.by_category).map(([k, v]) => ({
        name: t(CATEGORY_LABEL_KEYS[k]) || k, value: v
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading threat intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="threat-intel-page">
      <PageHeader
        title={t("threat.title")}
        description={t("threat.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="create-intel-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("threat.new")}
          </Button>
        }
      />

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">{t("threat.tab_dashboard")}</TabsTrigger>
          <TabsTrigger value="registry" data-testid="tab-registry">{t("threat.tab_registry")}</TabsTrigger>
          <TabsTrigger value="mitre" data-testid="tab-mitre">{t("threat.tab_mitre")}</TabsTrigger>
          <TabsTrigger value="threats" data-testid="tab-threats">{t("threat.tab_threats")}</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-border">
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-heading font-bold">{dashboard?.total || 0}</p>
                <p className="text-xs text-muted-foreground">{t("threat.total_intel")}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-heading font-bold text-red-500">{dashboard?.active || 0}</p>
                <p className="text-xs text-muted-foreground">{t("threat.active_threats")}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-heading font-bold text-red-500">{dashboard?.by_severity?.critical || 0}</p>
                <p className="text-xs text-muted-foreground">{t("critical")}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="pt-4 pb-4">
                <p className="text-2xl font-heading font-bold text-orange-500">{dashboard?.by_severity?.high || 0}</p>
                <p className="text-xs text-muted-foreground">{t("high")}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border" data-testid="severity-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t("threat.by_severity")}</CardTitle>
              </CardHeader>
              <CardContent>
                {severityChartData.some(d => d.value > 0) ? (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={severityChartData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                          {severityChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t("threat.no_data")}</div>
                )}
              </CardContent>
            </Card>
            <Card className="border-border" data-testid="category-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t("threat.by_category")}</CardTitle>
              </CardHeader>
              <CardContent>
                {categoryChartData.length > 0 ? (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={categoryChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                        <YAxis stroke="hsl(var(--muted-foreground))" />
                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                        <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t("threat.no_data")}</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Registry Tab */}
        <TabsContent value="registry" className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder={t("threat.search_ph")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="intel-search" />
            </div>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-36"><SelectValue placeholder={t("incidents.severity")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("threat.all_severity")}</SelectItem>
                {Object.entries(severityConfig).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-44"><SelectValue placeholder={t("category")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("threat.all_categories")}</SelectItem>
                {Object.entries(CATEGORY_LABEL_KEYS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterFramework} onValueChange={setFilterFramework}>
              <SelectTrigger className="w-44"><SelectValue placeholder={t("ctrl.framework")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("threat.all_frameworks")}</SelectItem>
                {orgFrameworks.map((f) => <SelectItem key={f.id} value={f.id}>{f.short}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((item, index) => {
                const CatIcon = categoryIcons[item.category] || ShieldAlert;
                return (
                  <motion.div key={item.intel_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, delay: index * 0.02 }}>
                    <Card className="border-border hover:border-primary/50 transition-colors cursor-pointer" onClick={() => setSelectedIntel(item)} data-testid={`intel-card-${item.intel_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <CatIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge variant="outline" className={statusConfig[item.status]?.color}>{t(STATUS_LABEL_KEYS[item.status] || item.status)}</Badge>
                                <Badge variant="outline" className={severityConfig[item.severity]?.color}>{t(severityConfig[item.severity]?.labelKey)}</Badge>
                                <Badge variant="secondary" className="capitalize">{t(CATEGORY_LABEL_KEYS[item.category]) || item.category}</Badge>
                                {(item.relevant_frameworks || []).map((fwId) => (
                                  <Badge
                                    key={fwId}
                                    variant="outline"
                                    className="text-[10px]"
                                    style={{ borderColor: FW_COLORS[fwId], color: FW_COLORS[fwId] }}
                                  >
                                    {FW_SHORT[fwId] || fwId}
                                  </Badge>
                                ))}
                              </div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                {item.source && <span>Source: {item.source}</span>}
                                {item.iocs?.length > 0 && <span>{item.iocs.length} IOCs</span>}
                                {item.mitre_techniques?.length > 0 && <span>{item.mitre_techniques.length} MITRE</span>}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <ShieldAlert className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{intel.length === 0 ? t("threat.no_intel") : t("threat.no_results")}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* MITRE ATT&CK Tab */}
        <TabsContent value="mitre" className="space-y-4">
          <Card className="border-border" data-testid="mitre-attack-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-red-500" />
                {t("threat.mitre_coverage")}
              </CardTitle>
              <CardDescription>{t("threat.mitre_desc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {MITRE_TACTICS.map(tactic => {
                  const id = tactic.split(" - ")[0];
                  const name = tactic.split(" - ")[1];
                  const count = (dashboard?.mitre_coverage || []).filter(m => m.technique?.startsWith(id)).reduce((acc, m) => acc + m.count, 0);
                  const hasData = count > 0;
                  return (
                    <div key={id} className={`p-3 rounded-lg border transition-colors ${hasData ? 'border-red-500/50 bg-red-500/5' : 'border-border bg-muted/20'}`}>
                      <p className="text-xs font-mono text-muted-foreground">{id}</p>
                      <p className="text-sm font-medium mt-1">{name}</p>
                      {hasData && <Badge variant="secondary" className="mt-2 text-xs">{count} intel</Badge>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Global Threats Tab */}
        <TabsContent value="threats" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border" data-testid="global-threats-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  {t("threat.known_threats")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(dashboard?.global_threats || []).map((t, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary">{t.category}</Badge>
                      <span className="font-medium text-sm">{t.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="border-border" data-testid="global-vulns-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BugIcon className="w-5 h-5 text-red-500" />
                  {t("threat.known_vulns")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(dashboard?.global_vulnerabilities || []).map((v, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary">{v.category}</Badge>
                      <span className="font-medium text-sm">{v.name}</span>
                    </div>
                    {v.cis_control && <p className="text-xs text-muted-foreground">CIS Control: {v.cis_control}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("threat.new_intel")}</DialogTitle>
            <DialogDescription>{t("threat.new_intel_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("threat.name_label")} *</Label>
              <Input value={newIntel.name} onChange={(e) => setNewIntel({ ...newIntel, name: e.target.value })} placeholder="e.g., APT29 Phishing Campaign" data-testid="intel-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("threat.category_label")}</Label>
                <Select value={newIntel.category} onValueChange={(v) => setNewIntel({ ...newIntel, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABEL_KEYS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("threat.severity_label")}</Label>
                <Select value={newIntel.severity} onValueChange={(v) => setNewIntel({ ...newIntel, severity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(severityConfig).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("threat.description_label")}</Label>
              <Textarea value={newIntel.description} onChange={(e) => setNewIntel({ ...newIntel, description: e.target.value })} placeholder={t("description")} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>{t("threat.source_label")}</Label>
              <Select value={newIntel.source} onValueChange={(v) => setNewIntel({ ...newIntel, source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">{t("threat.src.internal")}</SelectItem>
                  <SelectItem value="osint">{t("threat.src.osint")}</SelectItem>
                  <SelectItem value="vendor">{t("threat.src.vendor")}</SelectItem>
                  <SelectItem value="government">{t("threat.src.government")}</SelectItem>
                  <SelectItem value="industry">{t("threat.src.industry")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("threat.iocs_label")}</Label>
              <div className="flex gap-2">
                <Input value={iocInput} onChange={(e) => setIocInput(e.target.value)} placeholder={t("threat.iocs_ph")} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIOC())} />
                <Button type="button" variant="outline" onClick={addIOC} size="sm">{t("add")}</Button>
              </div>
              {newIntel.iocs.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {newIntel.iocs.map((ioc, i) => (
                    <Badge key={i} variant="secondary" className="font-mono text-xs">
                      {ioc}
                      <button className="ml-1 hover:text-destructive" onClick={() => setNewIntel({ ...newIntel, iocs: newIntel.iocs.filter((_, idx) => idx !== i) })}>x</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t("threat.mitre_label")}</Label>
              <div className="flex gap-2">
                <Select value={mitreInput} onValueChange={setMitreInput}>
                  <SelectTrigger><SelectValue placeholder={t("threat.mitre_label")} /></SelectTrigger>
                  <SelectContent>
                    {MITRE_TACTICS.map(tactic => <SelectItem key={tactic} value={tactic.split(" - ")[0]}>{tactic}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addMitre} size="sm">{t("add")}</Button>
              </div>
              {newIntel.mitre_techniques.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {newIntel.mitre_techniques.map((t, i) => (
                    <Badge key={i} variant="secondary" className="font-mono text-xs">
                      {t}
                      <button className="ml-1 hover:text-destructive" onClick={() => setNewIntel({ ...newIntel, mitre_techniques: newIntel.mitre_techniques.filter((_, idx) => idx !== i) })}>x</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-xs mb-2 block">{t("threat.frameworks_label")}</Label>
              <div className="flex flex-wrap gap-2">
                {orgFrameworks.map((f) => {
                  const selected = newIntel.relevant_frameworks.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setNewIntel({
                        ...newIntel,
                        relevant_frameworks: selected
                          ? newIntel.relevant_frameworks.filter((x) => x !== f.id)
                          : [...newIntel.relevant_frameworks, f.id],
                      })}
                      className="px-2.5 py-1 rounded-full text-xs border transition-colors"
                      style={{
                        borderColor: f.color,
                        backgroundColor: selected ? f.color : "transparent",
                        color: selected ? "#fff" : f.color,
                      }}
                    >
                      {f.short}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel")}</Button>
            <Button onClick={createIntel} data-testid="save-intel-btn">{t("threat.create_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedIntel} onOpenChange={() => setSelectedIntel(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedIntel && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className={statusConfig[selectedIntel.status]?.color}>{t(STATUS_LABEL_KEYS[selectedIntel.status] || selectedIntel.status)}</Badge>
                  <Badge variant="outline" className={severityConfig[selectedIntel.severity]?.color}>{t(severityConfig[selectedIntel.severity]?.labelKey)}</Badge>
                  <Badge variant="secondary">{t(CATEGORY_LABEL_KEYS[selectedIntel.category]) || selectedIntel.category}</Badge>
                </div>
                <DialogTitle>{selectedIntel.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">{t("threat.status_label")}:</Label>
                  <Select onValueChange={(v) => { updateStatus(selectedIntel.intel_id, v); setSelectedIntel({ ...selectedIntel, status: v }); }}>
                    <SelectTrigger className="w-40"><SelectValue placeholder={t(STATUS_LABEL_KEYS[selectedIntel.status] || selectedIntel.status)} /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABEL_KEYS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {selectedIntel.description && <div><Label className="text-xs text-muted-foreground">{t("description")}</Label><p className="text-sm mt-1">{selectedIntel.description}</p></div>}
                {selectedIntel.source && <div><Label className="text-xs text-muted-foreground">{t("threat.source_label_detail")}</Label><p className="text-sm mt-1 capitalize">{selectedIntel.source}</p></div>}
                {selectedIntel.iocs?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("threat.iocs_detail")}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">{selectedIntel.iocs.map((ioc, i) => <Badge key={i} variant="secondary" className="font-mono text-xs">{ioc}</Badge>)}</div>
                  </div>
                )}
                {selectedIntel.mitre_techniques?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t("threat.mitre_detail")}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">{selectedIntel.mitre_techniques.map((technique, i) => <Badge key={i} variant="outline" className="font-mono text-xs">{technique}</Badge>)}</div>
                  </div>
                )}
                <div><Label className="text-xs text-muted-foreground">{t("threat.created")}</Label><p className="text-sm mt-1">{new Date(selectedIntel.created_at).toLocaleString()}</p></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThreatIntelligence;
